import React from 'react';
import { Button } from '../../ui/Button/Button';
import { Field } from '../ui/Field';
import { Sheet } from '../ui/Sheet';
import type { Room } from '../../../lib/rcu/model';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface RoomSheetProps {
  isOpen: boolean;
  /** null creates a room, otherwise the one being edited. */
  room: Room | null;
  onClose: () => void;
}

/** Add or edit a room. Rooms are what the guest controls page groups by. */
export const RoomSheet: React.FC<RoomSheetProps> = ({ isOpen, room, onClose }) => {
  const addRoom = useRcuConfigStore((state) => state.addRoom);
  const updateRoom = useRcuConfigStore((state) => state.updateRoom);
  const report = useCommandFeedback();

  const [name, setName] = React.useState(room?.name ?? '');
  const [note, setNote] = React.useState(room?.note ?? '');

  const handleSave = (): void => {
    if (room === null) {
      if (report(addRoom({ name, note }), 'Room added.')) onClose();
      return;
    }
    if (report(updateRoom(room.id, { name, note }), 'Room saved.')) onClose();
  };

  return (
    <Sheet
      isOpen={isOpen}
      title={room === null ? 'Add room' : 'Edit room'}
      subtitle="Guests see one tab per room on the controls page."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={name.trim() === ''}>
            {room === null ? 'Add room' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field
          label="Room name"
          value={name}
          onChange={setName}
          placeholder="Bedroom"
          maxLength={30}
          autoFocus
        />
        <Field
          label="Note"
          value={note}
          onChange={setNote}
          placeholder="Optional, e.g. corner suite"
          maxLength={60}
        />
      </div>
    </Sheet>
  );
};

export default RoomSheet;
